import * as React from 'react';
import { FieldValues, UseFormReturn, Controller, FieldPath } from 'react-hook-form';
import { Field as ChakraField, Box } from '@chakra-ui/react';

// Re-export Field components
export const Field = ChakraField;

interface FormProps<T extends FieldValues = FieldValues> extends React.PropsWithChildren {
  form: UseFormReturn<T>;
  onSubmit: (data: T) => void | Promise<void>;
}

export const Form = React.forwardRef<HTMLFormElement, FormProps>(
  <T extends FieldValues = FieldValues>(
    { form, onSubmit, children, ...props }: FormProps<T>,
    ref: React.Ref<HTMLFormElement>
  ) => {
    return (
      <form ref={ref} onSubmit={form.handleSubmit(onSubmit)} noValidate {...props}>
        {children}
      </form>
    );
  }
) as <T extends FieldValues = FieldValues>(
  props: FormProps<T> & { ref?: React.Ref<HTMLFormElement> }
) => React.ReactElement;

(Form as React.ComponentType).displayName = 'Form';

interface FormFieldContextValue {
  name: string;
  formControl: UseFormReturn<FieldValues>;
}

const FormFieldContext = React.createContext<FormFieldContextValue | null>(null);

interface FormFieldProps<TFieldValues extends FieldValues = FieldValues> {
  name: FieldPath<TFieldValues>;
  form: UseFormReturn<TFieldValues>;
  children: React.ReactNode;
}

export function FormField<TFieldValues extends FieldValues = FieldValues>({
  name,
  form,
  children,
}: FormFieldProps<TFieldValues>) {
  return (
    <FormFieldContext.Provider value={{ name, formControl: form as UseFormReturn<FieldValues> }}>
      {children}
    </FormFieldContext.Provider>
  );
}

export function useFormField() {
  const context = React.useContext(FormFieldContext);
  if (!context) {
    throw new Error('useFormField must be used within a FormField');
  }
  return context;
}

interface FormItemProps extends React.HTMLAttributes<HTMLDivElement> {
  // Form item props
  children?: React.ReactNode;
}

export const FormItem = React.forwardRef<HTMLDivElement, FormItemProps>(
  ({ children, ...props }, ref) => {
    const { name, formControl } = useFormField();
    const fieldState = formControl.getFieldState(name);
    const hasError = !!fieldState.error;

    return (
      <ChakraField.Root ref={ref} {...(hasError ? { invalid: true } : {})} {...props}>
        {children}
      </ChakraField.Root>
    );
  }
);

FormItem.displayName = 'FormItem';

interface FormLabelProps {
  children: React.ReactNode;
  required?: boolean;
  className?: string;
}

export const FieldLabel = React.forwardRef<HTMLLabelElement, FormLabelProps>(
  ({ required, children, className: _className, ...props }, ref) => {
    const { name } = useFormField();

    return (
      <Box
        as={ChakraField.Label}
        ref={ref}
        fontSize="sm"
        fontWeight="medium"
        color="#374151"
        {...(props as Record<string, unknown>)}
        {...(name ? { htmlFor: name } : {})}
      >
        {children}
        {required && (
          <Box as="span" color="#EF4444" marginLeft={1}>
            *
          </Box>
        )}
      </Box>
    );
  }
);

FieldLabel.displayName = 'FieldLabel';

interface FormControlProps {
  children: (field: {
    name: string;
    value: unknown;
    onChange: (value: unknown) => void;
    onBlur: () => void;
    ref: React.Ref<HTMLElement>;
  }) => React.ReactElement;
}

export function FieldControl<TFieldValues extends FieldValues = FieldValues>({
  children,
}: FormControlProps) {
  const { name, formControl } = useFormField();

  return (
    <Controller
      name={name as FieldPath<TFieldValues>}
      control={formControl.control}
      render={({ field }) => children(field)}
    />
  );
}

interface FormDescriptionProps extends React.HTMLAttributes<HTMLParagraphElement> {
  // Form description props
  children?: React.ReactNode;
}

export const FormDescription = React.forwardRef<HTMLParagraphElement, FormDescriptionProps>(
  ({ children, ...props }, ref) => {
    return (
      <Box as={ChakraField.HelperText} ref={ref} {...(props as Record<string, unknown>)}>
        {children}
      </Box>
    );
  }
);

FormDescription.displayName = 'FormDescription';

interface FormMessageProps extends React.HTMLAttributes<HTMLParagraphElement> {
  // Form message props
  children?: React.ReactNode;
}

export const FormMessage = React.forwardRef<HTMLParagraphElement, FormMessageProps>(
  ({ children, ...props }, ref) => {
    const { name, formControl } = useFormField();
    const fieldState = formControl.getFieldState(name);
    const error = fieldState.error?.message;

    if (!error && !children) return null;

    return (
      <Box as={ChakraField.ErrorText} ref={ref} {...(props as Record<string, unknown>)}>
        {error || children}
      </Box>
    );
  }
);

FormMessage.displayName = 'FormMessage';

// Re-export Textarea from separate file
export { Textarea } from './textarea';

// SimpleFormField component for ResourceForm
interface SimpleFormFieldProps {
  label?: string;
  error?: string;
  required?: boolean;
  helperText?: string;
  gridColumn?: string;
  children: React.ReactNode;
}

export const SimpleFormField = React.forwardRef<HTMLDivElement, SimpleFormFieldProps>(
  ({ label, error, required, helperText, gridColumn, children }, ref) => {
    return (
      <ChakraField.Root ref={ref} invalid={!!error} gridColumn={gridColumn}>
        {label && (
          <Box as="label" fontSize="sm" fontWeight="medium" color={error ? '#EF4444' : '#374151'}>
            {label}
            {required && (
              <Box as="span" color="#EF4444" marginLeft={1}>
                *
              </Box>
            )}
          </Box>
        )}

        <Box marginTop={label ? '4px' : undefined}>{children}</Box>

        {error && (
          <Box fontSize="sm" color="#EF4444" marginTop={1}>
            {error}
          </Box>
        )}

        {helperText && !error && (
          <Box fontSize="sm" color="#6B7280" marginTop={1}>
            {helperText}
          </Box>
        )}
      </ChakraField.Root>
    );
  }
);

SimpleFormField.displayName = 'SimpleFormField';
